
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '@/firebase/config';
import { collection, getDocs, getDoc, doc, addDoc, onSnapshot, Timestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { SidebarProvider } from '@/components/ui/sidebar';
import OwnerSidebar from './OwnerSidebar';
import OwnerHeader from './OwnerHeader';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Eye, ChevronDown, Loader2 } from 'lucide-react';
import { useToast } from "@/hooks/use-toast";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const OwnerLiveSupport = () => {
  const navigate = useNavigate();
  const [schools, setSchools] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingView, setProcessingView] = useState(false);
  const [isWaitingForApproval, setIsWaitingForApproval] = useState(false);
  const [currentRequestId, setCurrentRequestId] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<{ id: string; role: string; name: string } | null>(null);
  const { toast } = useToast();
  const functions = getFunctions();
  const auth = getAuth();

  const initiateViewAs = (schoolId: string, role: string, schoolName: string) => {
    setPendingAction({ id: schoolId, role, name: schoolName });
  };

  // Cleanup listener when dialog closes
  useEffect(() => {
    if (!pendingAction) {
        setIsWaitingForApproval(false);
        setCurrentRequestId(null);
        setProcessingView(false);
    }
  }, [pendingAction]);

  // Listen for request status
  useEffect(() => {
    if (!currentRequestId || !pendingAction) return;

    const requestRef = doc(db, `schools/${pendingAction.id}/liveSupportRequests`, currentRequestId);
    const unsubscribe = onSnapshot(requestRef, (snapshot) => {
        const data = snapshot.data();
        if (data?.status === 'approved') {
            setIsWaitingForApproval(false);
            performImpersonation(); // Go ahead!
        } else if (data?.status === 'rejected') {
            setIsWaitingForApproval(false);
            setProcessingView(false);
            // Optionally close dialog or show error state
            toast({
                title: "Access Denied",
                description: "The school admin has denied your request.",
                variant: "destructive"
            });
            setCurrentRequestId(null); // Stop listening
        }
    });

    return () => unsubscribe();
  }, [currentRequestId, pendingAction]);

  const initiateRequest = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!pendingAction || !auth.currentUser) return;

    setProcessingView(true);
    
    // Create request
    try {
        const docRef = await addDoc(collection(db, `schools/${pendingAction.id}/liveSupportRequests`), {
            ownerId: auth.currentUser.uid,
            ownerEmail: auth.currentUser.email,
            targetRole: pendingAction.role,
            status: 'pending',
            createdAt: Timestamp.now()
        });
        setCurrentRequestId(docRef.id);
        setIsWaitingForApproval(true);
        // Don't setProcessingView(false) yet, we are "processing" until approved/rejected or cancelled
    } catch (error) {
        console.error("Error creating request:", error);
        toast({
            title: "Error",
            description: "Failed to create access request.",
            variant: "destructive"
        });
        setProcessingView(false);
    }
  };

  const performImpersonation = async () => {
    if (!pendingAction) return;
    setProcessingView(true); // Ensure spinner is active

    const { id: schoolId, role, name: schoolName } = pendingAction;
    
    try {
      // 1. Call Cloud Function to update claims
      const impersonateSchool = httpsCallable(functions, 'impersonateSchool');
      await impersonateSchool({ targetSchoolID: schoolId, targetRole: role });

      // 2. Force Token Refresh to get new claims
      if (auth.currentUser) {
        await auth.currentUser.getIdToken(true);
        // Update portal preference so App.tsx respects the new role
        localStorage.setItem('portal', role);
        
        toast({
          title: "Switching View",
          description: `Logging in to ${schoolName} as ${role}...`,
          duration: 3000,
        });

        // 3. Reload Page to let App.tsx pick up changes
        // Use setTimeout to allow toast to be seen briefly
        setTimeout(() => {
             window.location.href = '/dashboard'; 
        }, 1500);
      }
    } catch (error) {
      console.error("Failed to switch view:", error);
      toast({
        title: "Error",
        description: "Failed to switch view. Please try again.",
        variant: "destructive",
      });
      setProcessingView(false);
      setPendingAction(null);
    } 
  };

  useEffect(() => {
    const fetchSchools = async () => {
      try {
        const schoolsCol = collection(db, "schools");
        const schoolsSnapshot = await getDocs(schoolsCol);
        
        const schoolsData = await Promise.all(
          schoolsSnapshot.docs.map(async (schoolDoc) => {
            const detailsDoc = await getDoc(doc(db, "schools", schoolDoc.id, "schoolDetails", "main"));
            const data = detailsDoc.exists() ? detailsDoc.data() : {};
            return {
              id: schoolDoc.id,
              name: data.name || "Unknown School",
            };
          })
        );
        
        // Sort alphabetically by name
        schoolsData.sort((a, b) => a.name.localeCompare(b.name));
        setSchools(schoolsData);
      } catch (error) {
        console.error("Error fetching schools:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSchools();
  }, []);

  return (
    <SidebarProvider>
      <div className="flex h-screen bg-white dark:bg-slate-950 w-full overflow-hidden">
        <OwnerSidebar activeSection="Support & Tickets" onSectionChange={() => {}} userRole="owner" />
        <div className="flex-1 flex flex-col h-full overflow-hidden">
          <OwnerHeader />
          
          <div className="flex-1 flex flex-col h-full overflow-hidden">
            {/* Header Section */}
            <div className="px-8 py-6 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-white dark:bg-slate-950">
              <div className="flex items-center gap-4">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => navigate('/owner/support')}
                  className="gap-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 dark:text-slate-400 dark:hover:text-slate-200 dark:hover:bg-slate-800"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
                <div>
                  <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
                    Live School View
                  </h1>
                </div>
              </div>
              <div className="text-sm text-slate-500 dark:text-slate-400">
                {schools.length} Schools Registered
              </div>
            </div>

            {/* Content Section - Full Table */}
            <div className="flex-1 overflow-auto bg-white dark:bg-slate-950">
                <div className="min-w-full inline-block align-middle">
                  <div className="border-b border-slate-200 dark:border-slate-800">
                    {/* Table Header */}
                    <div className="sticky top-0 z-10 grid grid-cols-[60px_1fr_120px] gap-4 px-8 py-3 bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                      <div className="text-center">#</div>
                      <div>School Name</div>
                      <div className="text-right">Actions</div>
                    </div>

                    {/* Loading/Empty States */}
                    {loading ? (
                      <div className="flex items-center justify-center h-64">
                         <div className="h-6 w-6 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600"></div>
                      </div>
                    ) : schools.length === 0 ? (
                      <div className="text-center py-20 text-slate-500 dark:text-slate-400">
                        No schools found within the system.
                      </div>
                    ) : (
                      /* Table Body */
                      <div className="divide-y divide-slate-100 dark:divide-slate-800">
                        {schools.map((school, index) => (
                          <div 
                            key={school.id}
                            className="grid grid-cols-[60px_1fr_120px] gap-4 px-8 py-4 hover:bg-slate-50 dark:hover:bg-slate-900/50 transition-colors items-center"
                          >
                            <div className="text-center font-mono text-sm text-slate-400">
                              {index + 1}
                            </div>
                            <div className="text-sm font-medium text-slate-900 dark:text-slate-200">
                              {school.name}
                            </div>
                            <div className="text-right">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button 
                                    variant="outline"
                                    size="sm"
                                    className="h-8 gap-2 text-xs font-medium text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800 focus:ring-0 focus:ring-offset-0"
                                  >
                                    <Eye className="h-3 w-3" />
                                    View
                                    <ChevronDown className="h-3 w-3 opacity-50" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-56">
                                  <DropdownMenuItem onClick={() => initiateViewAs(school.id, 'admin', school.name)}>
                                    Log in as Admin
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => initiateViewAs(school.id, 'school_staff', school.name)}>
                                    Log in as Staff
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => initiateViewAs(school.id, 'student', school.name)}>
                                    Log in as Student
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => initiateViewAs(school.id, 'guardian', school.name)}>
                                    Log in as Guardian
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
            </div>
          </div>
        </div>

        <AlertDialog open={!!pendingAction} onOpenChange={(open) => !open && setPendingAction(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirm Impersonation</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to view <strong>{pendingAction?.name}</strong> as a <strong>{pendingAction?.role}</strong>?
                <br /><br />
                This will temporarily override your permissions. You can return to the dashboard at any time using the &quot;Exit School View&quot; button.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={processingView}>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={initiateRequest} 
                className="bg-emerald-600 hover:bg-emerald-700 ml-2"
                disabled={processingView || isWaitingForApproval}
              >
                {isWaitingForApproval ? (
                  <>
                     <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                     Waiting for Admin...
                  </>
                ) : processingView ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  'Request Access'
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </SidebarProvider>
  );
};

export default OwnerLiveSupport;
